// Spreadsheet-style import grid for prospect bulk import.
// Powers any element wrapped with [data-import-grid]. Supports:
//   - direct cell editing (contenteditable td)
//   - Excel paste: tab-separated rows expand into the grid
//   - add/remove row buttons
//   - serializing to a hidden textarea on submit so the server still receives
//     the same tab-separated payload

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();
const formBind = onceGuard();
const COLUMNS = ["Name", "Email", "Phone", "Source"];
const STARTING_ROWS = 5;
const MAX_PASTE_ROWS = 1000;

export function initImportGrid(root = document) {
  const grids = root.querySelectorAll("[data-import-grid]");
  grids.forEach((grid) => {
    if (!firstBind(grid)) return;

    const tbody = grid.querySelector("tbody");
    const hidden = grid.querySelector("[data-import-payload]");
    const addBtn = grid.querySelector("[data-import-add]");
    const clearBtn = grid.querySelector("[data-import-clear]");
    if (!tbody || !hidden) return;

    const newRow = () => {
      const tr = document.createElement("tr");
      tr.className = "border-b border-orange-50 last:border-0";
      COLUMNS.forEach(() => {
        const td = document.createElement("td");
        td.contentEditable = "true";
        td.className =
          "px-3 py-2 text-sm text-ink-900 focus:outline-none focus:bg-brand-50/40 min-w-[140px]";
        tr.appendChild(td);
      });
      const removeTd = document.createElement("td");
      removeTd.className = "px-2 py-2 text-right";
      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className =
        "text-ink-500 hover:text-red-600 transition-colors text-xs";
      removeBtn.textContent = "Remove";
      removeBtn.addEventListener("click", () => {
        tr.remove();
        sync();
      });
      removeTd.appendChild(removeBtn);
      tr.appendChild(removeTd);
      tbody.appendChild(tr);
      return tr;
    };

    const sync = () => {
      const lines = [];
      tbody.querySelectorAll("tr").forEach((tr) => {
        const cells = Array.from(tr.querySelectorAll("td"))
          .slice(0, COLUMNS.length)
          .map((td) => td.textContent.trim());
        if (cells.some((c) => c.length > 0)) {
          lines.push(cells.join("\t"));
        }
      });
      hidden.value = lines.join("\n");
    };

    // Excel paste — expand tab-separated content across rows/cells.
    tbody.addEventListener("paste", (event) => {
      const text = (event.clipboardData || window.clipboardData).getData(
        "text",
      );
      if (!text || (!text.includes("\t") && !text.includes("\n"))) return;
      event.preventDefault();

      const startCell = event.target.closest("td");
      if (!startCell) return;
      const startRow = startCell.parentElement;
      const startColIndex = Array.from(startRow.children).indexOf(startCell);

      const rows = text
        .split(/\r?\n/)
        .filter((row) => row.length > 0)
        .slice(0, MAX_PASTE_ROWS)
        .map((row) => row.split("\t"));

      let currentRow = startRow;
      rows.forEach((cells, rowOffset) => {
        if (!currentRow) currentRow = newRow();
        cells.forEach((value, colOffset) => {
          const targetIdx = startColIndex + colOffset;
          if (targetIdx >= COLUMNS.length) return;
          const targetCell = currentRow.children[targetIdx];
          if (targetCell) targetCell.textContent = value;
        });
        currentRow = currentRow.nextElementSibling;
        if (!currentRow && rowOffset < rows.length - 1) {
          currentRow = newRow();
        }
      });
      sync();
    });

    tbody.addEventListener("input", sync);

    if (addBtn) {
      addBtn.addEventListener("click", () => {
        newRow();
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener("click", () => {
        tbody.innerHTML = "";
        for (let i = 0; i < STARTING_ROWS; i++) newRow();
        sync();
      });
    }

    // Sync just before form submit so any pending edits land in the payload.
    // Guard against re-binding when initImportGrid runs again after htmx swaps.
    const form = grid.closest("form");
    if (form && formBind(form)) {
      form.addEventListener("submit", sync);
    }

    // Seed empty rows if grid is empty.
    if (tbody.children.length === 0) {
      for (let i = 0; i < STARTING_ROWS; i++) newRow();
    }
    sync();
  });
}
